"use client";

import { useState } from "react";

import AdAwareLink from "@/components/navigation/AdAwareLink";

import EyeIcon from "@/components/icons/EyeIcon";
import StarRating from "@/components/ui/StarRating/StarRating";
import { getOwnerIconSrc } from "@/lib/base/getOwnerIconSrc";
import { formatViewCount } from "@/lib/format/FormatViewCount";
import { formatRelativeTime } from "@/lib/time";
import type { BaseCardMetaMode, BaseWithRelations } from "@/types/base";

import styles from "./BaseCard.module.css";

interface Props {
  base: BaseWithRelations;
  showTime?: boolean;
  showMeta?: BaseCardMetaMode;
  showImage?: boolean;
  noRadiusOnMobile?: boolean;
  compactMobileInfo?: boolean;
}


function getMetaText(base: BaseWithRelations, showMeta: BaseCardMetaMode) {
  if (!showMeta) return null;

  const levelText = base.level?.level?.toUpperCase() ?? null;
  const categoryText = base.category?.title ?? null;

  if (showMeta === "level") return levelText;
  if (showMeta === "category") return categoryText;

  return [levelText, categoryText].filter(Boolean).join(" • ") || null;
}
const FALLBACK_BASE_IMAGE = "/images/fallback-base.webp";
const FALLBACK_OWNER_ICON = "/images/fallback-avatar.webp";

export default function BaseCardClient({
  base,
  showTime = false,
  showMeta = false,
  showImage = true,
  noRadiusOnMobile = false,
  compactMobileInfo = false,
}: Props) {
  const baseSlug = base.canonical_slug ?? base.slug;

  const [imageSrc, setImageSrc] = useState(
    base.layout_image_url?.trim() ||
      base.image_url?.trim() ||
      FALLBACK_BASE_IMAGE
  );

  const [ownerSrc, setOwnerSrc] = useState(getOwnerIconSrc(base.owner_icon));
  const metaText = getMetaText(base, showMeta);

  if (!baseSlug) return null;

  const href = `/bases/${encodeURIComponent(baseSlug)}`;
  const rating = base.rating_avg;
  const views = base.viewcount ?? 0;
  const ownerName = base.owner;

  return (
    <AdAwareLink href={href} prefetch={false} className={[styles.card, noRadiusOnMobile ? styles.noRadiusOnMobile : ""].filter(Boolean).join(" ")}>
      {showImage && (
        <div className={styles.imageWrapper}>
          <img
            src={imageSrc}
            alt={base.title}
            className={styles.image}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            onError={() => {
              if (imageSrc !== FALLBACK_BASE_IMAGE) {
                setImageSrc(FALLBACK_BASE_IMAGE);
              }
            }}
          />

          {showTime && base.created_at && (
            <span className={styles.timeBadge}>
              {formatRelativeTime(base.created_at)}
            </span>
          )}
        </div>
      )}

      <div className={[styles.info, compactMobileInfo ? styles.compactMobileInfo : ""].filter(Boolean).join(" ")}>
        <div className={styles.topRow}>
          <div className={styles.titleBlock}>
            <h3 className={styles.title}>{base.title}</h3>

            {metaText ? <p className={styles.meta}>{metaText}</p> : null}
          </div>

          {ownerName ? (
            <span className={styles.owner} title={ownerName}>
              <img
                src={ownerSrc}
                alt={ownerName}
                width={20}
                height={20}
                className={styles.ownerIcon}
                loading="lazy"
                decoding="async"
                onError={() => {
                  if (ownerSrc !== FALLBACK_OWNER_ICON) {
                    setOwnerSrc(FALLBACK_OWNER_ICON);
                  }
                }}
              />
            </span>
          ) : null}
        </div>

        <div className={styles.bottomRow}>
          {typeof rating === "number" && rating > 0 ? (
            <span className={styles.rating}>
              <StarRating value={rating} size={14} showValue={false} />
            </span>
          ) : (
            <span />
          )}

          {views > 0 && (
            <span className={styles.views}>
              <EyeIcon />
              {formatViewCount(views)}
            </span>
          )}
        </div>
      </div>
    </AdAwareLink>
  );
}